import {
    type DeepPartial,
    type MoneyMapAccountWithAccountDetails,
    type MoneyMapCategory,
    type MoneyMapRuleGroup as MoneyMapRuleGroupType,
    type MoneyMapRuleWithAccountDetails,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    EditIcon,
    FootnoteRegular,
    Icon,
    PlusIcon,
    redactPrivate,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { MoneyMapAccountRule } from './MoneyMapAccountRule';

interface MoneyMapRuleGroupHeaderProps {
    group: DeepPartial<MoneyMapRuleGroupType>;
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    onPress?: () => void;
}

const MoneyMapRuleGroupHeader = ({
    group,
    onPress,
}: MoneyMapRuleGroupHeaderProps) => {
    const { name } = group;
    const hoverStyle = onPress != null ? { cursor: 'pointer' } : {};

    return (
        <XStack
            alignItems="center"
            justifyContent="space-between"
            onPress={onPress}
            hoverStyle={hoverStyle}
            {...redactPrivate()}
        >
            <XStack gap={8} alignItems="center" flex={1} paddingLeft={4}>
                <YStack gap={4}>
                    <BodyMediumRegular flex={1}>{name}</BodyMediumRegular>
                </YStack>
            </XStack>
            <Icon color="$black" icon={EditIcon} size={16} />
        </XStack>
    );
};

interface MoneyMapRuleGroupProps {
    category: MoneyMapCategory;
    group: DeepPartial<MoneyMapRuleGroupType>;
    rules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    accountsById: Record<string, MoneyMapAccountWithAccountDetails>;
    onPressEdit?: () => void;
    onPressAddGoal: () => void;
    onPressRule?: (ruleId: string) => void;
    disabled?: boolean;
    isRuleInvalidGoalTarget?: (
        rule: DeepPartial<MoneyMapRuleWithAccountDetails>,
    ) => boolean;
}

export const MoneyMapRuleGroup = ({
    category,
    group,
    rules,
    moneyMap,
    accountsById,
    onPressEdit,
    onPressAddGoal,
    onPressRule,
    disabled = false,
    isRuleInvalidGoalTarget,
}: MoneyMapRuleGroupProps) => {
    // Check if percentages add up to 100%
    const totalPercentage = rules.reduce((sum, rule) => {
        const percentage = rule.percentage ?? 0;
        // Convert to number if it's a string
        const numericPercentage =
            typeof percentage === 'string'
                ? parseFloat(percentage)
                : percentage;
        return sum + numericPercentage;
    }, 0);

    // Empty groups (0%) are invalid - they should add up to 100%
    const hasInvalidPercentages =
        rules.length > 0 && Math.abs(totalPercentage - 1.0) > 0.01;
    return (
        <YStack
            borderRadius={12}
            padding={8}
            gap={12}
            borderWidth={1}
            borderColor="$black"
        >
            <MoneyMapRuleGroupHeader
                group={group}
                moneyMap={moneyMap}
                onPress={disabled ? undefined : onPressEdit}
            />
            {hasInvalidPercentages && (
                <FootnoteRegular color="$red1000">
                    Percentages must add up to 100%
                </FootnoteRegular>
            )}
            {rules.map((rule, index) => (
                <MoneyMapAccountRule
                    key={index}
                    category={category}
                    rule={rule}
                    moneyMap={moneyMap}
                    accountsById={accountsById}
                    showInvalidGoalTargetBalance={
                        isRuleInvalidGoalTarget?.(rule) ?? false
                    }
                    onPress={() => {
                        if (rule.id != null) {
                            onPressRule?.(rule.id);
                        } else if (rule.description != null) {
                            onPressRule?.(rule.description);
                        }
                    }}
                />
            ))}
            <View alignSelf="flex-start">
                <Button
                    text="Add Goal"
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.TINY}
                    icon={PlusIcon}
                    iconPosition="left"
                    width="auto"
                    onPress={onPressAddGoal}
                    disabled={disabled}
                />
            </View>
        </YStack>
    );
};
